import { useState } from "react";
import { Marca } from "@pessoas/ui";
import { Constelacao } from "./Constelacao.js";

/**
 * A ENTRADA.
 *
 * É a única tela que alguém de fora vê, e a primeira impressão do produto
 * inteiro. Composição em duas colunas: à esquerda a constelação, que diz o que
 * o sistema faz; à direita o formulário, com a densidade do resto do sistema.
 *
 * A mensagem de erro vem PRONTA do servidor e é sempre a mesma: distinguir
 * "CPF não existe" de "senha errada" transforma a tela num oráculo que revela
 * quem trabalha na empresa, a partir de uma lista de CPFs.
 */
const mascarar = (v: string): string => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

export type Empresa = { companyId: string; nome: string; papel: string };

const PAPEL: Record<string, string> = {
  admin: "Administração", dp: "Departamento pessoal", gestor: "Gestão",
  colaborador: "Colaborador", contador: "Contabilidade", auditor: "Auditoria",
  medical_officer: "Medicina do trabalho", sst: "Segurança do trabalho",
  recrutador: "Recrutamento",
};

export function Entrar({ aoEntrar }: { aoEntrar: () => void }) {
  const [cpf, setCpf] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [empresas, setEmpresas] = useState<Empresa[] | null>(null);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const r = await fetch("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cpf, senha }),
      });
      const corpo = await r.json() as {
        erro?: string; empresa?: { situacao: string; empresas?: Empresa[] };
      };
      if (!r.ok) { setErro(corpo.erro ?? "Não consegui entrar."); return; }

      // A escolha da empresa acontece DEPOIS de autenticar, nunca antes.
      if (corpo.empresa?.situacao === "escolher" && corpo.empresa.empresas) {
        setEmpresas(corpo.empresa.empresas);
        return;
      }
      if (corpo.empresa?.situacao === "sem_acesso") {
        setErro("Seu acesso não está ativo em nenhuma empresa. Fale com o RH.");
        return;
      }
      aoEntrar();
    } catch {
      setErro("Não consegui falar com o servidor. Tente de novo.");
    } finally {
      setEnviando(false);
    }
  };

  const escolher = (companyId: string) => {
    localStorage.setItem("eop:empresa", companyId);
    aoEntrar();
  };

  return (
    <div className="eop eop-entrada">
      {/* Painel da esquerda: a constelação. Some no celular, onde ela roubaria
          a tela do formulário sem acrescentar informação. */}
      <aside className="eop-entrada-arte" aria-hidden="true">
        <div className="eop-mesh"><span /><span /><span /></div>
        <div className="eop-grade eop-entrada-grade" />
        <Constelacao />
        {/*
          A frase fala com QUEM ESTÁ ENTRANDO.
          Na maior parte das vezes é o colaborador, abrindo o próprio holerite
          ou batendo o ponto. A versão anterior dizia que gente é o que a
          empresa tem de mais caro: escrita do ponto de vista de quem paga a
          folha, e lida por quem ESTÁ na folha. Ninguém quer ser recebido como
          uma linha de custo.
        */}
        <div className="eop-entrada-legenda">
          <p className="eop-entrada-frase">
            Cada hora que você trabalha tem registro,
            <em> e cada número tem explicação.</em>
          </p>
          <p className="eop-entrada-sub">
            Ponto, holerite, férias e saúde no mesmo lugar. Você abre qualquer
            valor e vê a conta inteira: a fórmula, os números que entraram e a
            regra que sustenta cada um.
          </p>
        </div>
      </aside>

      <main className="eop-entrada-form">
        <div className="eop-entrada-caixa surge">
          <Marca tamanho={32} />

          {empresas ? (
            <div className="mt-8">
              <p className="eyebrow">Onde você entra</p>
              <h1 className="titulo-pagina mt-2">Escolha a empresa</h1>
              <p className="mt-1.5 text-sm" style={{ color: "var(--muted-foreground)" }}>
                Seu CPF tem acesso a mais de uma.
              </p>
              <ul className="mt-5 space-y-2">
                {empresas.map((e, i) => (
                  <li key={e.companyId} className={`surge surge-${Math.min(i + 1, 4)}`}>
                    <button className="eop-empresa" onClick={() => escolher(e.companyId)}>
                      <span className="eop-empresa-nome">{e.nome}</span>
                      <span className="eop-empresa-papel">{PAPEL[e.papel] ?? e.papel}</span>
                      <span aria-hidden className="eop-empresa-seta">→</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <form onSubmit={enviar} className="mt-8">
              <p className="eyebrow">Acesso</p>
              <h1 className="titulo-pagina mt-2">Entrar</h1>
              <p className="mt-1.5 text-sm" style={{ color: "var(--muted-foreground)" }}>
                Use o seu CPF. Ele identifica você em todas as empresas onde
                você tem vínculo.
              </p>

              <div className="mt-6 space-y-4">
                <label className="block">
                  <span className="eyebrow block pb-1.5">CPF</span>
                  <input
                    className="campo num" value={cpf} inputMode="numeric"
                    autoComplete="username" autoFocus
                    onChange={(ev) => setCpf(mascarar(ev.target.value))}
                    placeholder="000.000.000-00" aria-label="CPF" required
                  />
                </label>
                <label className="block">
                  <span className="eyebrow block pb-1.5">Senha</span>
                  <input
                    className="campo" type="password" value={senha}
                    autoComplete="current-password"
                    onChange={(ev) => setSenha(ev.target.value)}
                    aria-label="Senha" required
                  />
                </label>
              </div>

              {erro && (
                <p role="alert" className="eop-erro">
                  <span aria-hidden className="sinal sinal-critico" />
                  <span>{erro}</span>
                </p>
              )}

              <button className="botao mt-6 w-full" data-variante="primario" disabled={enviando}>
                {enviando ? "Entrando" : "Entrar"}
                {!enviando && <span aria-hidden className="eop-botao-seta">→</span>}
              </button>

              <p className="mt-5 text-xs leading-relaxed" style={{ color: "var(--faint-foreground)" }}>
                O acesso é criado pelo RH da sua empresa. Se você ainda não tem
                senha, procure quem cuida do departamento pessoal.
              </p>
            </form>
          )}
        </div>

        <p className="eop-entrada-rodape carimbo">
          EnturOS · Pessoas
        </p>
      </main>
    </div>
  );
}
